import React, { useState, useEffect, useRef, useMemo } from "react";

import { Grid, SwipeableDrawer, Tooltip, useMediaQuery } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { AppSwitch } from "Components/Generic/AppSwitch";
import { useTranslation } from "react-i18next";
import { GoInfo } from "react-icons/go";
import { useHistory, useLocation } from "react-router-dom";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { GeneralPageEnum } from "Util/enums/general";
import { InventoryPageEnum } from "Util/enums/inventoryPage";
import { ResourcePageEnum } from "Util/enums/resourcePage";
import { WarehousePageEnum } from "Util/enums/warehouse";
import { checkResourceCan, FN_NAMES, hasPermission } from "Util/permissions";
import { isMoving } from "Util/validation";

import Leave from "../../../../components/Generic/Modal/Leave";
import GenericSidebar from "../../../../components/Generic/Sidebarnav";
import { useAppState } from "../../../../context/StateContext";
import { Company } from "../../../../models/company";
import { OnboardingStage } from "../../../../models/onboarding.stage";
import OnboardingService from "../../../../services/onBoarding.service";
import { SettingsNavItems } from "../../../../util/enums";
import { isAllied } from "../../../../util/isAllied";
import CommunicationCentreTab from "./CommunicationCentre";
import CompanyTab from "./Company";
import { ConfigurationsTab } from "./Configurations";
import FinancialTab from "./Financials";
import MilitaryTab from "./Military";
import { useActionSettings } from "./hooks/useAction";
import IntegrationsTab from "./Integrations";
import PeopleTab from "./People";
import ResourcesTab from "./Resources";
import SecurityTab from "./Security";
import useStyles from "./styles";
import TemplatesTab from "./Templates";
import * as TYPES from "./types";
import { SettingPageTab } from "./types";
import { SettingsPageTitle } from "./SettingsPageTitle";
import NotificationCentre from "../NotificationCentre";

type SubNavItem = {
  open: boolean;
  items: NavItem[];
};

type NavItem = {
  key?: string;
  label: string;
  currentSubPage?: string;
  subitems?: SubNavItem;
  iconRight?: boolean;
  action: () => void;
};

const deepEqual = (a: unknown, b: unknown) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return a === b;
  }
};

export const SettingsScreen = () => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const history = useHistory<{
    tabKey?:
      | "general"
      | "company"
      | "people"
      | "resources"
      | "configurations"
      | "integrations"
      | "security"
      | "financials"
      | "military"
      | "communicationcentre";
    subTabKey?: string;
    subSubTabKey?: string;
    tab?: string;
    tabPeople?: string;
    tabResource?: string;
  }>();
  const { currentUser, isFMVersion, userPermissions } = useAppState();
  const { isAllowedToShow } = useActionSettings();

  const [swipeableIsOpen, setSwipeableIsOpen] = useState<boolean>(false);
  const [onboardingStage, setOnboardingStage] = useState<OnboardingStage>();
  const [settingPermission, setSettingPermission] = useState<{
    company: boolean;
    members: {
      resourceCanView: boolean;
      accessRules: boolean;
      groups: boolean;
      members: boolean;
      teams: boolean;
    };
    resources: {
      resourceCanView: boolean;
      services: boolean;
      inventory: boolean;
      subContractors: boolean;
      equipments: boolean;
      checkListAndForms: boolean;
      instructions: boolean;
      materials: boolean;
      access: boolean;
    };
    templates: {
      resourceCanView: boolean;
      email: boolean;
      sms: boolean;
    };
    configurations: {
      resourceCanView: boolean;
      general: boolean;
      offices: boolean;
      branches: boolean;
      automation: boolean;
      warehousing: boolean;
      uploadCentre: boolean;
      automate: boolean;
      ports: boolean;
      hsCommodityCode: boolean;
    };
  }>({
    company: false,
    members: {
      resourceCanView: false,
      accessRules: false,
      groups: false,
      members: false,
      teams: false,
    },
    resources: {
      resourceCanView: false,
      services: false,
      inventory: false,
      subContractors: false,
      equipments: false,
      checkListAndForms: false,
      instructions: false,
      materials: false,
      access: false,
    },
    templates: {
      resourceCanView: false,
      email: false,
      sms: false,
    },
    configurations: {
      resourceCanView: false,
      general: false,
      offices: false,
      branches: false,
      automation: false,
      warehousing: false,
      uploadCentre: false,
      automate: false,
      ports: false,
      hsCommodityCode: false,
    },
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setSettingPermission({
        company: checkResourceCan(userPermissions, ResourceName.COMPANY, ResourceCanEnum.VIEW),
        members: {
          resourceCanView: checkResourceCan(
            userPermissions,
            ResourceName.PEOPLE,
            ResourceCanEnum.VIEW
          ),
          accessRules: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_SETTINGS_PEOPLE_ACCESS_RULES_VIEW
          ),
          groups: hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_GROUPS_VIEW),
          members: hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_MEMBERS_VIEW),
          teams: hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_TEAMS_VIEW),
        },
        resources: {
          resourceCanView: checkResourceCan(
            userPermissions,
            ResourceName.RESOURCES,
            ResourceCanEnum.VIEW
          ),
          services: hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SERVICES_VIEW),
          inventory: hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INVENTORY_VIEW),
          subContractors: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_RESOURCES_SUB_CONTRACTORS_VIEW
          ),
          equipments: hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_EQUIPMENTS_VIEW),
          checkListAndForms: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_RESOURCES_CHECKLIST_FORMS_VIEW
          ),
          instructions: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_RESOURCES_INSTRUCTIONS_VIEW
          ),
          materials: hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_MATERIALS_VIEW),
          access: hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_ACCESS_VIEW),
        },
        templates: {
          resourceCanView: checkResourceCan(
            userPermissions,
            ResourceName.TEMPLATES,
            ResourceCanEnum.VIEW
          ),
          email: hasPermission(userPermissions, FN_NAMES.FUNCTION_TEMPLATES_EMAIL_VIEW),
          sms: hasPermission(userPermissions, FN_NAMES.FUNCTION_TEMPLATES_SMS_VIEW),
        },
        configurations: {
          resourceCanView: checkResourceCan(
            userPermissions,
            ResourceName.CONFIGURATIONS,
            ResourceCanEnum.VIEW
          ),
          general: hasPermission(userPermissions, FN_NAMES.FUNCTION_CONFIGURATIONS_GENERAL_VIEW),
          offices: hasPermission(userPermissions, FN_NAMES.FUNCTION_CONFIGURATIONS_OFFICES_VIEW),
          branches: hasPermission(userPermissions, FN_NAMES.FUNCTION_CONFIGURATIONS_BRANCHES_VIEW),
          automation: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_CONFIGURATIONS_AUTOMATION_VIEW
          ),
          warehousing: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_CONFIGURATIONS_WAREHOUSING_VIEW
          ),
          uploadCentre: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_CONFIGURATIONS_UPLOAD_CENTRE_VIEW
          ),
          automate: hasPermission(userPermissions, FN_NAMES.FUNCTION_CONFIGURATIONS_AUTOMATE_VIEW),
          ports: hasPermission(userPermissions, FN_NAMES.FUNCTION_CONFIGURATIONS_PORTS_VIEW),
          hsCommodityCode: hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_CONFIGURATIONS_HS_COMMODITY_CODE_VIEW
          ),
        },
      });
    }
  }, [userPermissions]);

  const { refetch: refetchIsoCertificate } = useQuery(
    ["onboarding.get", currentUser?.companyId],
    () =>
      OnboardingService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!currentUser?.companyId,
      onSuccess: (response: AxiosResponse<OnboardingStage[]>) => {
        if (response.data && !!response.data.length) {
          setOnboardingStage(response.data[0]);
        }
      },
    }
  );

  const [tabsName, setTabsName] = useState<SettingPageTab>({ parent: "" });

  const [openPeopleSubNav, setOpenPeopleSubNav] = useState<boolean>(false);
  const [openIntegrationsSubNav, setOpenIntegrationsSubNav] = useState<boolean>(false);
  const [openResourceSubNav, setOpenResourceSubNav] = useState<boolean>(false);
  const [openTemplatesSubNav, setOpenTemplatesSubNav] = useState<boolean>(false);
  const [openConfigurationSubNav, setOpenConfigurationSubNav] = useState<boolean>(false);
  const [openConfigurationGeneralSubNav, setOpenConfigurationGeneralSubNav] =
    useState<boolean>(false);
  const [openInventorySubNav, setOpenInventorySubNav] = useState<boolean>(false);
  const [openWarehousingSubNav, setOpenWarehousingSubNav] = useState<boolean>(false);
  const [navType, setNavType] = useState<string>("settings");

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [tabBlocked, setTabBlocked] = useState<SettingPageTab>();

  const [openModal, setOpenModal] = useState<boolean>(false);

  const IsoTooltip = (
    <div className={classes.isoTooltip}>
      <GoInfo color="#FFF" size={30} />
      <span>{translate("asYouAddedAnItemThatDoesNotBelongToTheIso174511")}</span>
    </div>
  );

  const formatTabLevel = (tab: SettingPageTab | undefined): SettingPageTab | undefined => {
    if (!tab) return undefined;

    return {
      ...tab,
      parent: (tab.key ?? tab.parent ?? "") as string,
    };
  };

  const onChangeTab = (tab: SettingPageTab) => {
    if (hasUnsavedChanges) {
      setTabBlocked(tab);
      setOpenModal(true);
    } else {
      const baseTab = formatTabLevel(tab);
      const childTab = formatTabLevel(tab?.child);
      const grandchildTab = formatTabLevel(tab?.child?.child);

      const finalTab: SettingPageTab = {
        parent: baseTab?.parent ?? "",
        key: baseTab?.key,
        label: baseTab?.label,
        child: childTab
          ? {
              parent: childTab.parent,
              key: childTab.key,
              label: childTab.label,
              child: grandchildTab
                ? {
                    parent: grandchildTab.parent,
                    key: grandchildTab.key,
                    label: grandchildTab.label,
                  }
                : undefined,
            }
          : undefined,
      };

      setTabsName((prev) => (deepEqual(prev, finalTab) ? prev : finalTab));
    }
    setSwipeableIsOpen(false);
  };

  const pages = {
    general: settingPermission.company && <CompanyTab />,
    company: settingPermission.company && <CompanyTab />,
    military: currentUser?.company?.isMilitary && <MilitaryTab />,
    people: settingPermission.members.resourceCanView && (
      <PeopleTab tabName={tabsName.child?.key || tabsName.child?.parent || ""} />
    ),
    // communicationcentre: settingPermission.templates.resourceCanView && (
    //   <TemplatesTab tabName={tabsName.child?.key || tabsName.child?.parent || ""} />
    // ),
    financials: <FinancialTab />,
    security: <SecurityTab />,
    resources: settingPermission.resources.resourceCanView && (
      <ResourcesTab
        refetchIsoCertificate={refetchIsoCertificate}
        onboardingStage={onboardingStage}
        tabName={tabsName.child?.key || tabsName.child?.parent || ""}
        tabSubName={tabsName.child?.child?.key || tabsName.child?.child?.parent || ""}
      />
    ),
    configurations: settingPermission.configurations.resourceCanView && (
      <ConfigurationsTab
        tabName={tabsName.child?.key || tabsName.child?.parent || ""}
        subTabName={tabsName.child?.child?.key || tabsName.child?.child?.parent || ""}
        history={history}
      />
    ),
    communicationcentre: isMoving(currentUser?.company) ? <CommunicationCentreTab /> : null,
    integrations: (
      <IntegrationsTab
        tabName={tabsName.child?.key || tabsName.child?.parent || ""}
        onChange={(v) => setHasUnsavedChanges(v || false)}
      />
    ),
  };

  const currentContent = useMemo(() => {
    const key = (tabsName.parent || "") as keyof typeof pages;
    return pages[key] ?? null;
  }, [tabsName.parent, pages]);

  const getIntegrationsTab = () => {
    const items: NavItem[] = [];

    if (isAllied(currentUser?.company)) {
      items.push({
        label: translate("apiAllied"),
        key: "apiAllied",
        action: () =>
          onChangeTab({
            parent: translate(`integrations`),
            key: "integrations",
            child: {
              parent: translate("apiAllied"),
              key: "apiAllied",
            },
          }),
      });
    }

    if (items && items?.length > 0) {
      return isAllowedToShow({
        label: translate(`integrations`),
        key: "integrations",
        action: () => setOpenIntegrationsSubNav(!openIntegrationsSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openIntegrationsSubNav,
          items,
        },
      });
    }

    return {} as TYPES.NavItem;
  };

  const handleSubMenuPeopleByPermission = () => {
    const items: TYPES.NavItem[] = [
      isAllowedToShow({
        label: translate(`users`),
        key: "users",
        action: () =>
          onChangeTab({
            parent: translate(`members`),
            label: translate(`members`),
            key: "people",
            child: {
              parent: translate(`users`),
              key: "users",
            },
          }),
      }),
    ];

    if (settingPermission.members.accessRules) {
      items?.push({
        label: translate(`accessRules`),
        key: "accessRules",
        action: () =>
          onChangeTab({
            parent: translate(`members`),
            label: translate(`members`),
            key: "people",
            child: {
              parent: translate(`accessRules`),
              label: translate(`accessRules`),
              key: "accessRules",
            },
          }),
      });
    }

    if (settingPermission.members.groups && isMoving(currentUser?.company)) {
      items?.push({
        label: translate(`groups`),
        key: "groups",
        action: () =>
          onChangeTab({
            parent: translate(`members`),
            label: translate(`members`),
            key: "people",
            child: {
              parent: translate(`groups`),
              key: "groups",
            },
          }),
      });
    }

    if (settingPermission.members.members && isMoving(currentUser?.company)) {
      items?.push({
        label: translate(`members`),
        key: "members",
        action: () =>
          onChangeTab({
            parent: translate(`members`),
            label: translate(`members`),
            key: "people",
            child: {
              parent: translate(`members`),
              key: "members",
            },
          }),
      });
    }

    return items;
  };

  const handleSubMenuResourcesByPermission = () => {
    const items: TYPES.NavItem[] = [];

    if (settingPermission.resources.inventory) {
      items?.push({
        label: translate(`inventory`),
        key: "inventory",
        action: () => {
          setNavType("inventory");
          setOpenInventorySubNav(true);
        },
        iconRight: true,
      });
    }

    if (settingPermission.resources.checkListAndForms) {
      items?.push({
        label: translate("checklistForms"),
        key: "checklistForms",
        action: () =>
          onChangeTab({
            parent: translate(`resources`),
            key: "resources",
            child: {
              parent: translate("checklistForms"),
              key: ResourcePageEnum.CHECKLIST,
            },
          }),
      });
    }

    if (settingPermission.resources.equipments) {
      items?.push({
        label: translate("equipments"),
        key: "equipments",
        action: () =>
          onChangeTab({
            parent: translate(`resources`),
            key: "resources",
            child: {
              parent: translate("equipments"),
              key: ResourcePageEnum.EQUIPMENTS,
            },
          }),
      });
    }

    if (settingPermission.resources.subContractors) {
      items?.push({
        label: translate("subcontractors"),
        key: "subcontractors",
        action: () =>
          onChangeTab({
            parent: translate(`resources`),
            key: "resources",
            child: {
              parent: translate("subcontractors"),
              key: ResourcePageEnum.SUBCONTRACTORS,
            },
          }),
      });
    }

    if (settingPermission.resources.services) {
      items?.push({
        label: translate("services"),
        key: "services",
        action: () =>
          onChangeTab({
            parent: translate(`resources`),
            key: "resources",
            child: {
              parent: translate("services"),
              key: ResourcePageEnum.SERVICES,
            },
          }),
      });
    }

    if (settingPermission.resources.instructions) {
      items?.push({
        label: translate("instructions"),
        key: "instructions",
        action: () =>
          onChangeTab({
            parent: translate("resources"),
            key: "resources",
            child: {
              parent: translate("instructions"),
              key: ResourcePageEnum.INSTRUCTIONS,
            },
          }),
      });
    }

    if (settingPermission.resources.access) {
      items?.push({
        label: translate("access"),
        key: "access",
        action: () =>
          onChangeTab({
            parent: translate("resources"),
            key: "resources",
            child: {
              parent: translate("access"),
              key: ResourcePageEnum.ACCESS,
            },
          }),
      });
    }

    if (settingPermission.resources.materials) {
      items?.push({
        label: translate(`materials`),
        key: "materials",
        action: () =>
          onChangeTab({
            parent: translate(`resources`),
            key: "resources",
            child: {
              parent: translate(`materials`),
              key: ResourcePageEnum.MATERIAL,
            },
          }),
      });
    }

    return items;
  };

  const handleSubMenuTeamplatesByPermission = () => {
    const items: TYPES.NavItem[] = [];

    if (settingPermission.templates.email) {
      items?.push({
        label: translate("email"),
        key: "email",
        action: () =>
          onChangeTab({
            parent: translate("templates"),
            key: "templates",
            child: {
              parent: translate("email"),
              key: "email",
            },
          }),
      });
    }

    if (settingPermission.templates.sms) {
      items?.push({
        label: translate("sms"),
        key: "sms",
        action: () =>
          onChangeTab({
            parent: translate("templates"),
            key: "templates",
            child: {
              parent: translate("sms"),
              key: "sms",
            },
          }),
      });
    }

    return items;
  };

  const handleSubMenuConfigurationsByPermission = () => {
    const items: TYPES.NavItem[] = [];

    if (settingPermission.configurations.general) {
      items?.push({
        label: translate("general"),
        key: "general",
        action: () => setOpenConfigurationGeneralSubNav(!openConfigurationGeneralSubNav),
        currentSubPage: tabsName.child?.child?.parent,
        subitems: {
          open: openConfigurationGeneralSubNav,
          items: [
            {
              label: translate("preferences"),
              key: "preferences",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("preferences"),
                      key: GeneralPageEnum.PREFERENCES,
                    },
                  },
                }),
            },
            {
              label: translate("operationsApp"),
              key: "operationsApp",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("operationsApp"),
                      key: GeneralPageEnum.OPERATIONS_APP,
                    },
                  },
                }),
            },
            ...(Boolean(currentUser?.company?.isMilitary)
              ? [
                  {
                    label: translate("military"),
                    key: "military",
                    action: () =>
                      onChangeTab({
                        parent: translate(`configurations`),
                        key: "configurations",
                        child: {
                          parent: translate("general"),
                          key: "general",
                          child: {
                            parent: translate("military"),
                            key: GeneralPageEnum.MILITARY,
                          },
                        },
                      }),
                  },
                ]
              : []),
            {
              label: translate("network"),
              key: "network",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("network"),
                      key: GeneralPageEnum.NETWORK,
                    },
                  },
                }),
            },
            {
              label: translate("currencies"),
              key: "currencies",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("currencies"),
                      key: GeneralPageEnum.CURRENCIES,
                    },
                  },
                }),
            },
            {
              label: translate("measurements"),
              key: "measurements",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("measurements"),
                      key: GeneralPageEnum.MEASUREMENTS,
                    },
                  },
                }),
            },
            {
              label: translate("orderType"),
              key: "orderType",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("orderType"),
                      key: GeneralPageEnum.ORDER_TYPE,
                    },
                  },
                }),
            },
            {
              label: translate("leadSource"),
              key: "leadSource",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("leadSource"),
                      key: GeneralPageEnum.LEAD_SOURCE,
                    },
                  },
                }),
            },
            {
              label: translate("statuses"),
              key: "statuses",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("statuses"),
                      key: GeneralPageEnum.STATUS,
                    },
                  },
                }),
            },
            {
              label: translate("tasksType"),
              key: "tasksType",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("tasksType"),
                      key: GeneralPageEnum.TASK_TYPE,
                    },
                  },
                }),
            },
            {
              label: translate("languages"),
              key: "languages",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("languages"),
                      key: GeneralPageEnum.LANGUAGES,
                    },
                  },
                }),
            },
            {
              label: translate("ssccGeneration"),
              key: "ssccGeneration",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("ssccGeneration"),
                      key: GeneralPageEnum.SSCC_GENERATION,
                    },
                  },
                }),
            },
            {
              label: translate("tolerancePercentage"),
              key: "tolerancePercentage",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("tolerancePercentage"),
                      key: GeneralPageEnum.TOLERANCE_PORCENTAGE,
                    },
                  },
                }),
            },
            {
              label: translate("carriers"),
              key: "carriers",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("carriers"),
                      key: GeneralPageEnum.CARRIERS,
                    },
                  },
                }),
            },
            {
              label: translate("brands"),
              key: "brands",
              action: () =>
                onChangeTab({
                  parent: translate(`configurations`),
                  key: "configurations",
                  child: {
                    parent: translate("general"),
                    key: "general",
                    child: {
                      parent: translate("brands"),
                      key: GeneralPageEnum.BRANDS,
                    },
                  },
                }),
            },
          ],
        },
      });
    }

    if (settingPermission.configurations.warehousing) {
      items?.push({
        label: translate("warehousing"),
        key: "warehousing",
        action: () => setOpenWarehousingSubNav(!openWarehousingSubNav),
        currentSubPage: tabsName.child?.child?.parent,
        subitems: {
          open: openWarehousingSubNav,
          items: [
            {
              label: translate("storageBoxTypes"),
              key: "storageBoxTypes",
              action: () =>
                onChangeTab({
                  parent: translate("configurations"),
                  key: "configurations",
                  child: {
                    parent: translate("warehousing"),
                    key: "warehousing",
                    child: {
                      parent: translate("storageBoxTypes"),
                      key: WarehousePageEnum.STORAGE_BOX_TYPES,
                    },
                  },
                }),
            },
            {
              label: translate("warehouses"),
              key: "warehouses",
              action: () =>
                onChangeTab({
                  parent: translate("configurations"),
                  key: "configurations",
                  child: {
                    parent: translate("warehousing"),
                    key: "warehousing",
                    child: {
                      parent: translate("warehouses"),
                      key: WarehousePageEnum.WAREHOUSES,
                    },
                  },
                }),
            },
            {
              label: translate("storageBoxes"),
              key: "storageBoxes",
              action: () =>
                onChangeTab({
                  parent: translate("configurations"),
                  key: "configurations",
                  child: {
                    parent: translate("warehousing"),
                    key: "warehousing",
                    child: {
                      parent: translate("storageBoxes"),
                      key: WarehousePageEnum.STORAGE_BOXES,
                    },
                  },
                }),
            },
          ],
        },
      });
    }

    if (settingPermission.configurations.branches) {
      items?.push({
        label: translate("branches"),
        key: "branches",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate("configurations"),
            key: "configurations",
            child: {
              parent: translate("branches"),
              key: "branches",
            },
          }),
      });
    }

    if (settingPermission.configurations.automation) {
      items?.push({
        label: translate("automation"),
        key: "automation",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate("configurations"),
            key: "configurations",
            child: {
              parent: translate("automation"),
              key: "automation",
            },
          }),
      });
    }

    if (settingPermission.configurations.hsCommodityCode) {
      items?.push({
        label: translate("hsCommodityCode"),
        key: "hsCommodityCode",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate(`configurations`),
            key: "configurations",
            child: {
              parent: translate("hsCommodityCode"),
              key: "hsCommodityCode",
            },
          }),
      });
    }

    if (settingPermission.configurations.ports) {
      items?.push({
        label: translate("ports"),
        key: "ports",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate("configurations"),
            key: "configurations",
            child: {
              parent: translate("ports"),
              key: "ports",
            },
          }),
      });
    }

    if (settingPermission.configurations.automate) {
      items?.push({
        label: translate("automate"),
        key: "automate",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate("configurations"),
            key: "configurations",
            child: {
              parent: translate("automate"),
              key: "automate",
            },
          }),
      });
    }

    if (settingPermission.configurations.uploadCentre) {
      items?.push({
        label: translate("uploadCentre"),
        key: "uploadCentre",
        currentSubPage: tabsName.child?.parent,
        action: () =>
          onChangeTab({
            parent: translate("configurations"),
            key: "configurations",
            child: {
              parent: translate("uploadCentre"),
              key: "uploadCentre",
            },
          }),
      });
    }

    return items;
  };

  const handleSettingsNavByPermission = () => {
    const listOfItems: TYPES.NavItem[] = [];

    if (settingPermission.company) {
      listOfItems?.push({
        label: translate("company"),
        key: "company",
        action: () =>
          onChangeTab({
            parent: translate("company"),
            key: "company",
          }),
      });
    }

    if (currentUser?.company?.isMilitary) {
      listOfItems?.push({
        label: translate("military", { defaultValue: "Military" }),
        key: "military",
        action: () =>
          onChangeTab({
            parent: translate("military", { defaultValue: "Military" }),
            key: "military",
          }),
      });
    }

    if (settingPermission.members.resourceCanView) {
      listOfItems?.push({
        label: translate(`members`),
        key: "people",
        action: () => setOpenPeopleSubNav(!openPeopleSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openPeopleSubNav,
          items: isMoving(currentUser?.company)
            ? handleSubMenuPeopleByPermission()
            : [
                isAllowedToShow({
                  label: translate(`users`),
                  key: "users",
                  action: () =>
                    onChangeTab({
                      parent: translate(`members`),
                      label: translate(`members`),
                      key: "people",
                      child: {
                        parent: translate(`users`),
                        key: "users",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate(`accessRules`),
                  key: "accessRules",
                  action: () =>
                    onChangeTab({
                      parent: translate(`members`),
                      label: translate(`members`),
                      key: "people",
                      child: {
                        parent: translate(`accessRules`),
                        label: translate(`accessRules`),
                        key: "accessRules",
                      },
                    }),
                }),
                {
                  label: translate(`groups`),
                  key: "groups",
                  action: () =>
                    onChangeTab({
                      parent: translate(`members`),
                      label: translate(`members`),
                      key: "people",
                      child: {
                        parent: translate(`groups`),
                        key: "groups",
                      },
                    }),
                },
                isAllowedToShow({
                  label: translate(`members`),
                  key: "members",
                  action: () =>
                    onChangeTab({
                      parent: translate(`members`),
                      label: translate(`members`),
                      key: "people",
                      child: {
                        parent: translate(`members`),
                        key: "members",
                      },
                    }),
                }),
              ],
        },
      });
    }

    if (settingPermission.resources.resourceCanView) {
      listOfItems?.push({
        label: translate(`resources`),
        key: "resources",
        action: () => setOpenResourceSubNav(!openResourceSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openResourceSubNav,
          items: isMoving(currentUser?.company)
            ? handleSubMenuResourcesByPermission()
            : [
                {
                  label: translate(`inventory`),
                  key: "inventory",
                  action: () => {
                    setNavType("inventory");
                    setOpenInventorySubNav(true);
                  },
                },
                {
                  label: translate("checklistForms"),
                  key: "checklistForms",
                  action: () =>
                    onChangeTab({
                      parent: translate(`resources`),
                      key: "resources",
                      child: {
                        parent: translate("checklistForms"),
                        key: ResourcePageEnum.CHECKLIST,
                      },
                    }),
                },
                {
                  label: translate("equipments"),
                  key: "equipments",
                  action: () =>
                    onChangeTab({
                      parent: translate(`resources`),
                      key: "resources",
                      child: {
                        parent: translate("equipments"),
                        key: ResourcePageEnum.EQUIPMENTS,
                      },
                    }),
                },
                {
                  label: translate("subcontractors"),
                  key: "subcontractors",
                  action: () =>
                    onChangeTab({
                      parent: translate(`resources`),
                      key: "resources",
                      child: {
                        parent: translate("subcontractors"),
                        key: ResourcePageEnum.SUBCONTRACTORS,
                      },
                    }),
                },
                {
                  label: translate("services"),
                  key: "services",
                  action: () =>
                    onChangeTab({
                      parent: translate(`resources`),
                      key: "resources",
                      child: {
                        parent: translate("services"),
                        key: ResourcePageEnum.SERVICES,
                      },
                    }),
                },
                {
                  label: translate("instructions"),
                  key: "instructions",
                  action: () =>
                    onChangeTab({
                      parent: translate("resources"),
                      key: "resources",
                      child: {
                        parent: translate("instructions"),
                        key: ResourcePageEnum.INSTRUCTIONS,
                      },
                    }),
                },
                {
                  label: translate("access"),
                  key: "access",
                  action: () =>
                    onChangeTab({
                      parent: translate("resources"),
                      key: "resources",
                      child: {
                        parent: translate("access"),
                        key: ResourcePageEnum.ACCESS,
                      },
                    }),
                },
                {
                  label: translate(`materials`),
                  key: "materials",
                  action: () =>
                    onChangeTab({
                      parent: translate(`resources`),
                      key: "resources",
                      child: {
                        parent: translate(`materials`),
                        key: ResourcePageEnum.MATERIAL,
                      },
                    }),
                },
              ],
        },
      });
    }

    listOfItems?.push(
      isAllowedToShow({
        label: translate("security"),
        key: "security",
        action: () =>
          onChangeTab({
            parent: translate(`security`),
            key: "security",
          }),
      })
    );

    if (settingPermission.templates.resourceCanView) {
      listOfItems?.push({
        label: translate("templates"),
        key: "notification",
        action: () => setOpenTemplatesSubNav(!openTemplatesSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openTemplatesSubNav,
          items: isMoving(currentUser?.company)
            ? handleSubMenuTeamplatesByPermission()
            : [
                {
                  label: translate("email"),
                  key: "email",
                  action: () =>
                    onChangeTab({
                      parent: translate("templates"),
                      key: "templates",
                      child: {
                        parent: translate("email"),
                        key: "email",
                      },
                    }),
                },
                {
                  label: translate("sms"),
                  key: "sms",
                  action: () =>
                    onChangeTab({
                      parent: translate("templates"),
                      key: "templates",
                      child: {
                        parent: translate("sms"),
                        key: "sms",
                      },
                    }),
                },
              ],
        },
      });
    }

    if (isMoving(currentUser?.company)) {
      listOfItems?.push({
        label: translate("communicationCentre"),
        key: "communicationCentre",
        action: () => {
          onChangeTab({
            parent: "communicationCentre",
            key: "communicationCentre",
          });
        },
      });
    }

    if (settingPermission.configurations.resourceCanView) {
      listOfItems?.push({
        label: translate(`configurations`),
        key: "configurations",
        action: () => {
          setNavType("configurations");
          setOpenConfigurationSubNav(true);
        },
        iconRight: true,
      });
    }

    listOfItems?.push(
      isAllowedToShow({
        label: translate("financials"),
        key: "financials",
        action: () =>
          onChangeTab({
            parent: translate(`financials`),
            key: "financials",
          }),
      }),
      getIntegrationsTab()
    );

    return { listOfItems };
  };

  const settingsNav = {
    listOfItems: [
      isAllowedToShow({
        label: translate("company"),
        key: "company",
        action: () =>
          onChangeTab({
            parent: translate("company"),
            key: "company",
          }),
      }),
      ...(currentUser?.company?.isMilitary
        ? [
            {
              label: translate("military", { defaultValue: "Military" }),
              key: "military",
              action: () =>
                onChangeTab({
                  parent: translate("military", { defaultValue: "Military" }),
                  key: "military",
                }),
            },
          ]
        : []),
      isAllowedToShow({
        label: translate(`people`),
        key: "people",
        action: () => setOpenPeopleSubNav(!openPeopleSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openPeopleSubNav,
          items: [
            isAllowedToShow({
              label: translate(`users`),
              key: "users",
              action: () =>
                onChangeTab({
                  parent: translate(`people`),
                  key: "people",
                  child: {
                    parent: translate(`users`),
                    key: "users",
                  },
                }),
            }),
            isAllowedToShow({
              label: translate(`roles`),
              key: "roles",
              action: () =>
                onChangeTab({
                  parent: translate(`people`),
                  key: "people",
                  child: {
                    parent: translate(`roles`),
                    key: "roles",
                  },
                }),
            }),
            isAllowedToShow({
              label: translate(`groups`),
              action: () =>
                onChangeTab({
                  parent: translate(`people`),
                  child: {
                    parent: translate(`groups`),
                  },
                }),
            }),
            isAllowedToShow({
              label: translate(`members`),
              key: "members",
              action: () =>
                onChangeTab({
                  parent: translate(`people`),
                  key: "people",
                  child: {
                    parent: translate(`members`),
                    key: "members",
                  },
                }),
            }),
          ],
        },
      }),
      isAllowedToShow({
        label: translate("security"),
        key: "security",
        action: () =>
          onChangeTab({
            parent: translate(`security`),
            key: "security",
          }),
      }),
      isAllowedToShow({
        label: translate(`resources`),
        key: "resources",
        action: () => setOpenResourceSubNav(!openResourceSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openResourceSubNav,
          items: [
            {
              label: translate(`inventory`),
              key: "inventory",
              action: () => {
                setNavType("inventory");
                setOpenInventorySubNav(true);
              },
              iconRight: true,
            },
            {
              label: translate("checklistForms"),
              key: "checklistForms",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate("checklistForms"),
                    key: ResourcePageEnum.CHECKLIST,
                  },
                }),
            },
            {
              label: translate("equipments"),
              key: "equipments",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate("equipments"),
                    key: ResourcePageEnum.EQUIPMENTS,
                  },
                }),
            },
            {
              label: translate("subcontractors"),
              key: "subcontractors",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate("subcontractors"),
                    key: ResourcePageEnum.SUBCONTRACTORS,
                  },
                }),
            },
            {
              label: translate("services"),
              key: "services",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate("services"),
                    key: ResourcePageEnum.SERVICES,
                  },
                }),
            },
            {
              label: translate("instructions"),
              key: "instructions",
              action: () =>
                onChangeTab({
                  parent: translate("resources"),
                  key: "resources",
                  child: {
                    parent: translate("instructions"),
                    key: ResourcePageEnum.INSTRUCTIONS,
                  },
                }),
            },
            {
              label: translate("access"),
              key: "access",
              action: () =>
                onChangeTab({
                  parent: translate("resources"),
                  key: "resources",
                  child: {
                    parent: translate("access"),
                    key: ResourcePageEnum.ACCESS,
                  },
                }),
            },
            {
              label: translate(`materials`),
              key: "materials",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`materials`),
                    key: ResourcePageEnum.MATERIAL,
                  },
                }),
            },
          ],
        },
      }),
      isAllowedToShow({
        label: translate("templates"),
        key: "notification",
        action: () => setOpenTemplatesSubNav(!openTemplatesSubNav),
        currentSubPage: tabsName.child?.parent,
        subitems: {
          open: openTemplatesSubNav,
          items: [
            {
              label: translate("email"),
              key: "email",
              action: () =>
                onChangeTab({
                  parent: translate("templates"),
                  key: "templates",
                  child: {
                    parent: translate("email"),
                    key: "email",
                  },
                }),
            },
            {
              label: translate("sms"),
              key: "sms",
              action: () =>
                onChangeTab({
                  parent: translate("templates"),
                  key: "templates",
                  child: {
                    parent: translate("sms"),
                    key: "sms",
                  },
                }),
            },
          ],
        },
      }),
//       {
//         label: translate("communicationCentre"),
//         key: "communicationCentre",
//         action: () => {
//           onChangeTab({
//             parent: "communicationcentre",
//             key: "communicationcentre",
//           });
//         },
//       },
      isAllowedToShow({
        label: translate(`configurations`),
        key: "configurations",
        action: () => {
          setNavType("configurations");
          setOpenConfigurationSubNav(true);
        },
        iconRight: true,
      }),
      isAllowedToShow({
        label: translate("financials"),
        key: "financials",
        action: () =>
          onChangeTab({
            parent: translate(`financials`),
            key: "financials",
          }),
      }),
      getIntegrationsTab(),
    ],
  };

  const inventoryNav = {
    listOfItems: [
      {
        label: translate(`inventory`),
        key: "inventory",
        action: () => setOpenInventorySubNav(!openInventorySubNav),
        currentSubPage: tabsName.child?.child?.parent,
        subitems: {
          open: openInventorySubNav,
          items: [
            {
              label: translate("itemList"),
              key: "itemList",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate("itemList"),
                      key: InventoryPageEnum.ITEM,
                    },
                  },
                }),
            },
            {
              label: translate(`location`),
              key: "location",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`location`),
                      key: InventoryPageEnum.LOCATION,
                    },
                  },
                }),
            },
            {
              label: translate(`packagingType`),
              key: "packagingType",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`packagingType`),
                      key: InventoryPageEnum.PACKAGING,
                    },
                  },
                }),
            },
            {
              label: translate(`methodOfPreparation`),
              key: "methodOfPreparation",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`methodOfPreparation`),
                      key: InventoryPageEnum.PREPARATION,
                    },
                  },
                }),
            },
            {
              label: translate(`modeType`),
              key: "modeType",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`modeType`),
                      key: InventoryPageEnum.MODE,
                    },
                  },
                }),
            },
            {
              label: translate(`exception`),
              key: "exception",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`exception`),
                      key: InventoryPageEnum.EXCEPTION,
                    },
                  },
                }),
            },
            {
              label: translate(`exceptionLocationCode`),
              key: "exceptionLocationCode",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`exceptionLocationCode`),
                      key: InventoryPageEnum.EXCEPTION_LOCATION,
                    },
                  },
                }),
            },
            {
              label: translate(`itemInstructions`),
              key: "itemInstructions",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`itemInstructions`),
                      key: InventoryPageEnum.SPECIAL_INSTRUCTIONS,
                    },
                  },
                }),
            },
            {
              label: translate(`itemDestination`),
              key: "itemDestination",
              action: () =>
                onChangeTab({
                  parent: translate(`resources`),
                  key: "resources",
                  child: {
                    parent: translate(`inventory`),
                    key: ResourcePageEnum.INVENTORY,
                    child: {
                      parent: translate(`itemDestination`),
                      key: InventoryPageEnum.DESTINATION,
                    },
                  },
                }),
            },
          ],
        },
      } as NavItem,
    ],
    extraComponents: [
      {
        label: translate("iso174511"),
        component: [
          <div
            key="uploaded-inventory"
            className="d-flex align-items-center justify-content-between mt-2"
          >
            {onboardingStage && !onboardingStage?.inventoryWasUploaded ? (
              <>
                <label>{translate("active")}</label>
                <AppSwitch disabled checked />
              </>
            ) : (
              <>
                <label>{translate("inactive")}</label>
                <Tooltip title={IsoTooltip} placement="top">
                  <div>
                    <AppSwitch disabled checked={false} />
                  </div>
                </Tooltip>
              </>
            )}
          </div>,
        ],
      },
    ],
  };

  const configurationNav = {
    listOfItems: [
      isAllowedToShow({
        label: translate(`configurations`),
        key: "configurations",
        action: () => setOpenConfigurationSubNav(!openConfigurationSubNav),
        currentSubPage: tabsName.child?.child?.parent || tabsName.child?.parent,
        subitems: {
          open: openConfigurationSubNav,
          items: isMoving(currentUser?.company)
            ? handleSubMenuConfigurationsByPermission()
            : [
                isAllowedToShow({
                  label: translate("general"),
                  key: "general",
                  action: () => setOpenConfigurationGeneralSubNav(!openConfigurationGeneralSubNav),
                  currentSubPage: tabsName.child?.child?.parent,
                  subitems: {
                    open: openConfigurationGeneralSubNav,
                    items: [
                      isAllowedToShow({
                        label: translate("preferences"),
                        key: "preferences",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("preferences"),
                                key: GeneralPageEnum.PREFERENCES,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("operationsApp"),
                        key: "operationsApp",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("operationsApp"),
                                key: GeneralPageEnum.OPERATIONS_APP,
                              },
                            },
                          }),
                      }),
                      ...(Boolean(currentUser?.company?.isMilitary)
                        ? [
                            isAllowedToShow({
                              label: translate("military"),
                              key: "military",
                              action: () =>
                                onChangeTab({
                                  parent: translate(`configurations`),
                                  key: "configurations",
                                  child: {
                                    parent: translate("general"),
                                    key: "general",
                                    child: {
                                      parent: translate("military"),
                                      key: GeneralPageEnum.MILITARY,
                                    },
                                  },
                                }),
                            }),
                          ]
                        : []),
                      isAllowedToShow({
                        label: translate("network"),
                        key: "network",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("network"),
                                key: GeneralPageEnum.NETWORK,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("currencies"),
                        key: "currencies",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("currencies"),
                                key: GeneralPageEnum.CURRENCIES,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("measurements"),
                        key: "measurements",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("measurements"),
                                key: GeneralPageEnum.MEASUREMENTS,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("orderType"),
                        key: "orderType",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("orderType"),
                                key: GeneralPageEnum.ORDER_TYPE,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("leadSource"),
                        key: "leadSource",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("leadSource"),
                                key: GeneralPageEnum.LEAD_SOURCE,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("statuses"),
                        key: "statuses",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("statuses"),
                                key: GeneralPageEnum.STATUS,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("tasksType"),
                        key: "tasksType",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("tasksType"),
                                key: GeneralPageEnum.TASK_TYPE,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("languages"),
                        key: "languages",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("languages"),
                                key: GeneralPageEnum.LANGUAGES,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("ssccGeneration"),
                        key: "ssccGeneration",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("ssccGeneration"),
                                key: GeneralPageEnum.SSCC_GENERATION,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("tolerancePercentage"),
                        key: "tolerancePercentage",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("tolerancePercentage"),
                                key: GeneralPageEnum.TOLERANCE_PORCENTAGE,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("carriers"),
                        key: "carriers",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("carriers"),
                                key: GeneralPageEnum.CARRIERS,
                              },
                            },
                          }),
                      }),
                      isAllowedToShow({
                        label: translate("brands"),
                        key: "brands",
                        action: () =>
                          onChangeTab({
                            parent: translate(`configurations`),
                            key: "configurations",
                            child: {
                              parent: translate("general"),
                              key: "general",
                              child: {
                                parent: translate("brands"),
                                key: GeneralPageEnum.BRANDS,
                              },
                            },
                          }),
                      }),
                    ],
                  },
                }),
                isAllowedToShow({
                  label: translate("warehousing"),
                  key: "warehousing",
                  action: () => setOpenWarehousingSubNav(!openWarehousingSubNav),
                  currentSubPage: tabsName.child?.child?.parent,
                  subitems: {
                    open: openWarehousingSubNav,
                    items: [
                      {
                        label: translate("storageBoxTypes"),
                        key: "storageBoxTypes",
                        action: () =>
                          onChangeTab({
                            parent: translate("configurations"),
                            key: "configurations",
                            child: {
                              parent: translate("warehousing"),
                              key: "warehousing",
                              child: {
                                parent: translate("storageBoxTypes"),
                                key: WarehousePageEnum.STORAGE_BOX_TYPES,
                              },
                            },
                          }),
                      },
                      {
                        label: translate("warehouses"),
                        key: "warehouses",
                        action: () =>
                          onChangeTab({
                            parent: translate("configurations"),
                            key: "configurations",
                            child: {
                              parent: translate("warehousing"),
                              key: "warehousing",
                              child: {
                                parent: translate("warehouses"),
                                key: WarehousePageEnum.WAREHOUSES,
                              },
                            },
                          }),
                      },
                      {
                        label: translate("storageBoxes"),
                        key: "storageBoxes",
                        action: () =>
                          onChangeTab({
                            parent: translate("configurations"),
                            key: "configurations",
                            child: {
                              parent: translate("warehousing"),
                              key: "warehousing",
                              child: {
                                parent: translate("storageBoxes"),
                                key: WarehousePageEnum.STORAGE_BOXES,
                              },
                            },
                          }),
                      },
                    ],
                  },
                }),
                isAllowedToShow({
                  label: translate("branches"),
                  key: "branches",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("branches"),
                        key: "branches",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("automation"),
                  key: "automation",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("automation"),
                        key: "automation",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("hsCommodityCode"),
                  key: "hsCommodityCode",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate(`configurations`),
                      key: "configurations",
                      child: {
                        parent: translate("hsCommodityCode"),
                        key: "hsCommodityCode",
                      },
                    }),
                }),
                ...(isFMVersion
                  ? [
                      isAllowedToShow({
                        label: translate("fleet"),
                        key: translate("configurations"),
                        currentSubPage: tabsName.child?.parent,
                        action: () =>
                          onChangeTab({
                            parent: translate("configurations"),
                            key: "configurations",
                            child: {
                              parent: translate("fleet"),
                              key: "fleet",
                            },
                          }),
                      }),
                    ]
                  : []),
                isAllowedToShow({
                  label: translate("offices"),
                  key: "offices",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("offices"),
                        key: "offices",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("ports"),
                  key: "ports",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("ports"),
                        key: "ports",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("notifyParty"),
                  key: "notifyParty",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("notifyParty"),
                        key: "notifyParty",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("signature"),
                  key: "signature",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("signature"),
                        key: "signature",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("automate"),
                  key: "automate",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("automate"),
                        key: "automate",
                      },
                    }),
                }),
                isAllowedToShow({
                  label: translate("uploadCentre"),
                  key: "uploadCentre",
                  currentSubPage: tabsName.child?.parent,
                  action: () =>
                    onChangeTab({
                      parent: translate("configurations"),
                      key: "configurations",
                      child: {
                        parent: translate("uploadCentre"),
                        key: "uploadCentre",
                      },
                    }),
                }),
              ],
        },
      }),
    ],
  };

  const ensureString = (val: unknown, fallback: string): string =>
    typeof val === "string" && val.length ? val : fallback;

  const location = useLocation();
  const initRef = useRef(false);

  useEffect(() => {
    const stNew = location.state as
      | {
          tabKey?:
            | "general"
            | "company"
            | "people"
            | "resources"
            | "configurations"
            | "integrations"
            | "security"
            | "communicationcentre"
            | "financials"
            | "military";
          subTabKey?: string;
          subSubTabKey?: string;
        }
      | undefined;

    if (!stNew && initRef.current) return;

    if (stNew?.tabKey === "resources") {
      const sub = ensureString(stNew.subTabKey, ResourcePageEnum.CHECKLIST);
      const sub2 = ensureString(stNew.subSubTabKey || "", "");

      onChangeTab({
        parent: translate("resources"),
        key: "resources",
        child: {
          parent: sub,
          key: sub,
          child: sub2 ? { parent: sub2, key: sub2 } : undefined,
        },
      });

      setOpenResourceSubNav(true);
      if (sub === ResourcePageEnum.INVENTORY) {
        setNavType("inventory");
        setOpenInventorySubNav(true);
      }

      initRef.current = true;
      return;
    }

    if (stNew?.tabKey === "configurations") {
      const sub = ensureString(stNew.subTabKey, "general");
      const sub2 = ensureString(stNew.subSubTabKey || "", "");

      onChangeTab({
        parent: translate("configurations"),
        key: "configurations",
        child: {
          parent: sub,
          key: sub,
          child: sub2 ? { parent: sub2, key: sub2 } : undefined,
        },
      });

      setNavType("configurations");
      setOpenConfigurationSubNav(true);
      if (sub === "general") setOpenConfigurationGeneralSubNav(true);

      initRef.current = true;
      return;
    }

    if (stNew?.tabKey === "people") {
      const sub = ensureString(stNew.subTabKey, "users");
      onChangeTab({
        parent: translate("members"),
        key: "people",
        child: {
          parent: sub,
          key: sub,
        },
      });
      setOpenPeopleSubNav(true);
      initRef.current = true;
      return;
    }

    if (stNew?.tabKey === "security") {
      onChangeTab({ parent: translate("security"), key: "security" });
      initRef.current = true;
      return;
    }
    if (stNew?.tabKey === "financials") {
      onChangeTab({ parent: translate("financials"), key: "financials" });
      initRef.current = true;
      return;
    }
    if (stNew?.tabKey === "integrations") {
      onChangeTab({ parent: translate("integrations"), key: "integrations" });
      initRef.current = true;
      return;
    }
    if (stNew?.tabKey === "general") {
      onChangeTab({ parent: translate("company"), key: "company" });
      initRef.current = true;
      return;
    }

    if (stNew?.tabKey === "company") {
      onChangeTab({ parent: translate("company"), key: "company" });
      initRef.current = true;
      return;
    }
    if (stNew?.tabKey === "military") {
      onChangeTab({
        parent: translate("military", { defaultValue: "Military" }),
        key: "military",
      });
      initRef.current = true;
      return;
    }

    if (stNew?.tabKey === "communicationcentre") {
      onChangeTab({
        parent: "communicationcentre",
        key: "communicationcentre",
      });
      initRef.current = true;
      return;
    }

    if (!initRef.current) {
      const stLegacy = location.state as any;
      if (stLegacy?.tab === translate("people")) {
        const sub = ensureString(stLegacy.tabPeople, translate("users"));
        onChangeTab({
          parent: translate("members"),
          key: "people",
          child: { parent: sub, key: sub },
        });
        setOpenPeopleSubNav(true);
        initRef.current = true;
        return;
      }
      if (stLegacy?.tab === translate("resources") && stLegacy.tabResource) {
        const resSub = ensureString(stLegacy.tabResource, "");
        onChangeTab({
          parent: translate("resources"),
          key: "resources",
          child: { parent: resSub, key: resSub },
        });
        setOpenResourceSubNav(true);
        if (resSub === ResourcePageEnum.INVENTORY) {
          setNavType("inventory");
          setOpenInventorySubNav(true);
        }
        initRef.current = true;
        return;
      }

      onChangeTab({ parent: translate("company"), key: "company" });
      initRef.current = true;
    }
  }, [location, translate, history, isAllowedToShow]);

  const checkTabNames = React.useCallback(() => {
    if (history) {
      const { location } = history;

      if (location && (location.state as any)?.tab === translate(`people`)) {
        if (tabsName.parent !== translate(`people`)) {
          history.replace({ ...history.location, state: {} });
        }
      }
    }
  }, [history, tabsName.parent, translate]);

  useEffect(() => {
    document.getElementById("content")?.scrollTop;
    checkTabNames();
  }, [tabsName, checkTabNames]);

  const getNavList = () => {
    switch (navType) {
      case "settings": {
        const nav = isMoving(currentUser?.company) ? handleSettingsNavByPermission() : settingsNav;
        return nav;
      }
      case "configurations":
        return configurationNav;
      case "inventory":
        return inventoryNav;
      default:
        return settingsNav;
    }
  };

  const navList = useMemo(getNavList, [
    navType,
    tabsName,
    openPeopleSubNav,
    openIntegrationsSubNav,
    openResourceSubNav,
    openTemplatesSubNav,
    openConfigurationSubNav,
    openConfigurationGeneralSubNav,
    openInventorySubNav,
    openWarehousingSubNav,
    currentUser?.company,
    isFMVersion,
    settingPermission,
    translate,
  ]);

  const renderSwipeableDrawer = () => {
    return (
      <SwipeableDrawer
        anchor="top"
        open={swipeableIsOpen}
        onClose={() => setSwipeableIsOpen(false)}
        onOpen={() => setSwipeableIsOpen(true)}
        SwipeAreaProps={{
          className: classes.swipeArea,
        }}
      >
        <GenericSidebar
          currentPage={tabsName.parent}
          list={navList}
          backButton={
            navType !== "settings"
              ? {
                  isShowing: true,
                  action: () => {
                    setNavType("settings");
                  },
                }
              : undefined
          }
        />
      </SwipeableDrawer>
    );
  };

  return (
    <div>
      {renderSwipeableDrawer()}
      <SettingsPageTitle
        company={currentUser?.company || ({} as Company)}
        tab={tabsName}
        swipeableIsOpen={swipeableIsOpen}
        setSwipeableIsOpen={setSwipeableIsOpen}
      />
      <Leave
        open={openModal}
        onClose={(isConfirmed) => {
          if (isConfirmed && tabBlocked) {
            setTabsName(tabBlocked);
            setHasUnsavedChanges(false);
            setTabBlocked(undefined);
          }
          setOpenModal(false);
        }}
      />
      {isMoving(currentUser?.company) ? (
        <Grid item xs={6} sm={11} md={11} lg={8}>
          <div id="content" className={classes.content}>
            {currentContent}
          </div>
        </Grid>
      ) : (
        <Grid container direction="row" justifyContent="center">
          <Grid
            style={{
              display: useMediaQuery("(max-width: 600px)") ? "none" : "block",
            }}
            item
            xs={4}
            sm={3}
            md={3}
            lg={2}
          >
            <GenericSidebar
              currentPage={tabsName.parent}
              list={navList}
              backButton={
                navType !== "settings"
                  ? {
                      isShowing: true,
                      action: () => {
                        setNavType("settings");
                      },
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={9} md={9} lg={10}>
            <div id="content" className={classes.content}>
              {currentContent}
            </div>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default SettingsScreen;
